/**
 * T24 — Music Scheduler (Remote Console + Device Details) pure helpers.
 *
 * Integration only: the Player owns scheduling behavior. This module only
 * shapes/validates the Fleet command payload and parses device-CONFIRMED
 * snapshots. A draft is never treated as confirmed, and nothing here ever
 * resends update_kk_music_schedule.
 */

import {
  REPORT_KK_MUSIC_SCHEDULE_COMMAND,
  UPDATE_KK_MUSIC_SCHEDULE_COMMAND,
} from "@/features/ui/constants";
import type { DeviceDetailSection } from "@/features/devices/deviceDetailDisplay";
import type {
  MusicScheduleCommandBody,
  MusicScheduleSlotBody,
} from "@/features/devices/devicesApi";

export { REPORT_KK_MUSIC_SCHEDULE_COMMAND, UPDATE_KK_MUSIC_SCHEDULE_COMMAND };

export const MUSIC_SCHEDULE_NOT_REPORTED = "Not reported";

export const MUSIC_SCHEDULE_COPY = {
  reading: "Reading Music Scheduler…",
  unsupported:
    "Music Scheduler is not reported as supported by this device. Settings were not sent.",
  unavailable:
    "Could not read the Music Scheduler. Confirm is disabled so unknown values are not sent.",
  notReported:
    "Music Scheduler is not reported by the device. Confirm is disabled until the device reports its schedule.",
  confirmationUnavailable:
    "Music Scheduler confirmation unavailable. Draft values are not applied.",
} as const;

export const CHANNEL_ID_MAX_LENGTH = 128;
export const CHANNEL_NAME_MAX_LENGTH = 100;
/**
 * TODO(T24): replace free-text Channel ID/Name inputs with an authorized
 * picker wired to the zone's channel eligibility. The existing SA
 * `useListChannelsQuery` (GET /channels) is the global catalog and is NOT
 * scoped to zone eligibility, so it is intentionally not used here.
 */
export const CHANNEL_ID_PATTERN = /^[A-Za-z0-9][A-Za-z0-9_.:-]*$/;

const TIME_RE = /^([01]\d|2[0-3]):([0-5]\d)(?::[0-5]\d)?$/;

export type ConfirmedMusicSchedule = {
  enabled: boolean;
  /** null when the device/Fleet did not report a usable revision. */
  scheduleRevision: number | null;
  slot1: MusicScheduleSlotBody | null;
  slot2: MusicScheduleSlotBody | null;
};

export type MusicScheduleSlotDraft = {
  channelId: string;
  channelName: string;
  /** HH:mm, or null when unset. */
  time: string | null;
};

export type MusicScheduleDraft = {
  enabled: boolean;
  slot1: MusicScheduleSlotDraft;
  slot2: MusicScheduleSlotDraft;
};

export const EMPTY_SLOT_DRAFT: MusicScheduleSlotDraft = {
  channelId: "",
  channelName: "",
  time: null,
};

/** Normalise "HH:mm" or "HH:mm:ss" to "HH:mm"; anything else → null. */
export function normalizeScheduleTime(raw: unknown): string | null {
  if (typeof raw !== "string") return null;
  const m = raw.trim().match(TIME_RE);
  return m ? `${m[1]}:${m[2]}` : null;
}

type SlotParse =
  | { ok: true; slot: MusicScheduleSlotBody | null }
  | { ok: false };

function parseSlot(raw: unknown): SlotParse {
  if (raw === null || raw === undefined) return { ok: true, slot: null };
  if (typeof raw !== "object") return { ok: false };
  const r = raw as Record<string, unknown>;
  const channelId = typeof r.channelId === "string" ? r.channelId.trim() : "";
  const channelName =
    typeof r.channelName === "string" ? r.channelName.trim() : "";
  const time = normalizeScheduleTime(r.time);
  if (!channelId || !time) return { ok: false };
  return { ok: true, slot: { channelId, channelName, time } };
}

/**
 * Parse a Fleet-confirmed Music Scheduler snapshot. Returns null (= "Not
 * reported") when absent or malformed. A malformed slot invalidates the whole
 * snapshot so a partial/guessed schedule is never hydrated as confirmed.
 */
export function parseConfirmedMusicSchedule(
  raw: unknown
): ConfirmedMusicSchedule | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  if (typeof r.enabled !== "boolean") return null;
  const s1 = parseSlot(r.slot1);
  const s2 = parseSlot(r.slot2);
  if (!s1.ok || !s2.ok) return null;
  const rev = r.scheduleRevision;
  const scheduleRevision =
    typeof rev === "number" && Number.isSafeInteger(rev) && rev >= 0 ? rev : null;
  return {
    enabled: r.enabled,
    scheduleRevision,
    slot1: s1.slot,
    slot2: s2.slot,
  };
}

export function confirmedMusicScheduleFromDetail(
  player: DeviceDetailSection | null | undefined
): ConfirmedMusicSchedule | null {
  return parseConfirmedMusicSchedule(player?.musicSchedule);
}

/** confirmed revision + 1 when known, else 1. Never a larger jump. */
export function nextScheduleRevision(
  confirmed: ConfirmedMusicSchedule | null | undefined
): number {
  const rev = confirmed?.scheduleRevision;
  if (typeof rev === "number" && Number.isSafeInteger(rev) && rev >= 0) {
    return rev + 1;
  }
  return 1;
}

export function slotDraftFromConfirmed(
  slot: MusicScheduleSlotBody | null
): MusicScheduleSlotDraft {
  if (!slot) return { ...EMPTY_SLOT_DRAFT };
  return {
    channelId: slot.channelId,
    channelName: slot.channelName,
    time: slot.time,
  };
}

export function draftFromConfirmed(
  confirmed: ConfirmedMusicSchedule
): MusicScheduleDraft {
  return {
    enabled: confirmed.enabled,
    slot1: slotDraftFromConfirmed(confirmed.slot1),
    slot2: slotDraftFromConfirmed(confirmed.slot2),
  };
}

export type SlotKey = "slot1" | "slot2";
export type SlotFieldErrors = Partial<
  Record<"channelId" | "channelName" | "time", string>
>;
export type MusicScheduleErrors = {
  slot1?: SlotFieldErrors;
  slot2?: SlotFieldErrors;
  form?: string;
};

function isSlotBlank(d: MusicScheduleSlotDraft): boolean {
  return !d.channelId.trim() && !d.channelName.trim() && !d.time;
}

function validateSlot(d: MusicScheduleSlotDraft): SlotFieldErrors {
  const errors: SlotFieldErrors = {};
  const id = d.channelId.trim();
  const name = d.channelName.trim();
  if (!id) errors.channelId = "Enter a channel ID";
  else if (id.length > CHANNEL_ID_MAX_LENGTH || !CHANNEL_ID_PATTERN.test(id)) {
    errors.channelId = "Channel ID has invalid characters or is too long";
  }
  if (!name) errors.channelName = "Enter a channel name";
  else if (
    name.length > CHANNEL_NAME_MAX_LENGTH ||
    // eslint-disable-next-line no-control-regex
    /[\u0000-\u001f\u007f]/.test(name)
  ) {
    errors.channelName = "Channel name is invalid or too long";
  }
  if (!d.time || !TIME_RE.test(d.time)) errors.time = "Select a time";
  return errors;
}

export type MusicScheduleBuildResult =
  | { ok: true; body: MusicScheduleCommandBody }
  | { ok: false; errors: MusicScheduleErrors };

/**
 * Validate a draft and build the Fleet payload. A slot is either fully blank
 * (sent as null) or fully valid. Enabling requires at least one slot.
 */
export function buildMusicScheduleCommandBody(
  draft: MusicScheduleDraft,
  confirmed: ConfirmedMusicSchedule | null | undefined
): MusicScheduleBuildResult {
  const errors: MusicScheduleErrors = {};
  const slots: Record<SlotKey, MusicScheduleSlotBody | null> = {
    slot1: null,
    slot2: null,
  };
  (["slot1", "slot2"] as const).forEach((key) => {
    const d = draft[key];
    if (isSlotBlank(d)) return;
    const slotErrors = validateSlot(d);
    if (Object.keys(slotErrors).length > 0) {
      errors[key] = slotErrors;
      return;
    }
    slots[key] = {
      channelId: d.channelId.trim(),
      channelName: d.channelName.trim(),
      time: d.time as string,
    };
  });
  if (draft.enabled && !slots.slot1 && !slots.slot2 && !errors.slot1 && !errors.slot2) {
    errors.form = "Add at least one slot before enabling the Music Scheduler";
  }
  if (errors.slot1 || errors.slot2 || errors.form) return { ok: false, errors };
  return {
    ok: true,
    body: {
      enabled: draft.enabled === true,
      scheduleRevision: nextScheduleRevision(confirmed),
      slot1: slots.slot1,
      slot2: slots.slot2,
    },
  };
}

export function formatMusicScheduleSlot(
  slot: MusicScheduleSlotBody | null | undefined
): string {
  if (!slot) return "empty";
  const label = slot.channelName || slot.channelId;
  return `${label} (${slot.channelId}) at ${slot.time}`;
}

/** Device Details / copy: confirmed-only summary. null → "Not reported". */
export function formatMusicScheduleSummary(
  confirmed: ConfirmedMusicSchedule | null | undefined
): string {
  if (!confirmed) return MUSIC_SCHEDULE_NOT_REPORTED;
  if (!confirmed.enabled) return "Off";
  return `On — Slot 1: ${formatMusicScheduleSlot(
    confirmed.slot1
  )}; Slot 2: ${formatMusicScheduleSlot(confirmed.slot2)}`;
}

/** Pending copy: requested (draft) vs device-confirmed. Never merges the two. */
export function pendingMusicScheduleCopy(args: {
  requested: MusicScheduleCommandBody | null;
  confirmed: ConfirmedMusicSchedule | null;
}): string | null {
  if (!args.requested) return null;
  const req = args.requested.enabled
    ? `On — Slot 1: ${formatMusicScheduleSlot(
        args.requested.slot1
      )}; Slot 2: ${formatMusicScheduleSlot(args.requested.slot2)}`
    : "Off";
  const conf = args.confirmed
    ? formatMusicScheduleSummary(args.confirmed)
    : "unknown";
  return `Music Scheduler requested: ${req}. Device confirmed: ${conf}.`;
}

// ---------------------------------------------------------------------------
// On-open report decision (fail closed on missing Fleet capability evidence)
// ---------------------------------------------------------------------------

export function fleetAdvertisesMusicScheduleReport(
  commandSupport: string[] | null | undefined
): boolean {
  return (
    Array.isArray(commandSupport) &&
    commandSupport.includes(REPORT_KK_MUSIC_SCHEDULE_COMMAND)
  );
}

export type MusicScheduleOpenAction =
  | { type: "wait" }
  | { type: "skip"; reason: "no-device" | "commands-disabled" | "already-started" }
  | { type: "unavailable" }
  | { type: "unsupported" }
  | { type: "report" };

export function decideMusicScheduleOpenRefresh(input: {
  deviceId: string | null;
  commandsEnabled: boolean;
  reportStarted: boolean;
  isFetching: boolean;
  isError: boolean;
  isSuccess: boolean;
  commandSupport: string[] | null | undefined;
}): MusicScheduleOpenAction {
  if (!input.deviceId) return { type: "skip", reason: "no-device" };
  if (!input.commandsEnabled) return { type: "skip", reason: "commands-disabled" };
  if (input.reportStarted) return { type: "skip", reason: "already-started" };
  if (input.isFetching) return { type: "wait" };
  if (input.isError) return { type: "unavailable" };
  if (!input.isSuccess) return { type: "wait" };
  if (!fleetAdvertisesMusicScheduleReport(input.commandSupport)) {
    return { type: "unsupported" };
  }
  return { type: "report" };
}

/** On-open read only. Never includes a settings payload. */
export function reportKkMusicScheduleRequest(deviceId: string): {
  deviceId: string;
  command: typeof REPORT_KK_MUSIC_SCHEDULE_COMMAND;
} {
  return { deviceId, command: REPORT_KK_MUSIC_SCHEDULE_COMMAND };
}

export type PostMusicConfirmAction =
  | { type: "apply-confirmed" }
  | { type: "confirmation-unavailable" };

/**
 * After a failed/timed-out update_kk_music_schedule: reconcile to the Fleet
 * confirmed snapshot or show confirmation-unavailable. Never a resend.
 */
export function decidePostMusicConfirmFailure(input: {
  fleetReachable: boolean;
  confirmed: ConfirmedMusicSchedule | null;
}): PostMusicConfirmAction {
  return input.fleetReachable && input.confirmed
    ? { type: "apply-confirmed" }
    : { type: "confirmation-unavailable" };
}

export function canConfirmMusicSchedule(args: {
  commandsEnabled: boolean;
  confirmedAvailable: boolean;
  fieldIsTouched: boolean;
  isMutating: boolean;
  disabled?: boolean;
}): boolean {
  return (
    args.commandsEnabled === true &&
    args.confirmedAvailable === true &&
    args.fieldIsTouched === true &&
    args.isMutating !== true &&
    args.disabled !== true
  );
}
